"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { useSession } from "next-auth/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { toast } from "react-toastify";
import { Button, Dialog, DialogBody, DialogTitle } from "@/components";
import {
  acknowledgeTerms,
  getTermsAcknowledgmentStatus,
} from "@/lib/services/legal";
import { getErrorMessage } from "@/lib";

export default function TermsAcknowledgmentModal() {
  const { data: session } = useSession();
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTermsVersion, setActiveTermsVersion] = useState<string | null>(
    null,
  );
  const [activeTermsUrl, setActiveTermsUrl] = useState<string | null>(null);
  const statusLoadedRef = useRef(false);
  const lastLoadedTokenRef = useRef<string | null>(null);

  useEffect(() => {
    const accessToken = session?.accessToken ?? null;

    if (!accessToken) {
      statusLoadedRef.current = false;
      lastLoadedTokenRef.current = null;
      return;
    }

    const tokenChanged = lastLoadedTokenRef.current !== accessToken;
    if (tokenChanged) {
      statusLoadedRef.current = false;
    }

    if (statusLoadedRef.current) {
      return;
    }

    statusLoadedRef.current = true;
    lastLoadedTokenRef.current = accessToken;

    const loadStatus = async () => {
      const response = await getTermsAcknowledgmentStatus();
      if (response?.error) {
        statusLoadedRef.current = false;
        return;
      }

      setActiveTermsVersion(response?.version ?? null);
      setActiveTermsUrl(response?.contentUrl ?? null);
      if (response?.requiresAcknowledgment) {
        setIsOpen(true);
      }
    };

    loadStatus();
  }, [session?.accessToken]);

  async function handleAcknowledge(source: string) {
    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    const response = await acknowledgeTerms(source);

    if (response?.error) {
      toast.error(getErrorMessage(response) || "Failed to save acknowledgment");
      setIsSubmitting(false);
      return;
    }

    setIsOpen(false);
    setIsSubmitting(false);
  }

  return (
    <Dialog
      size="2xl"
      centered
      open={isOpen}
      onClose={() => undefined}
      className={"md:!w-[560px] !min-w-[310px] !w-full"}>
      <DialogTitle className="flex items-center justify-between gap-4">
        <span>Terms & Conditions Update</span>
        <button
          type="button"
          onClick={() => handleAcknowledge("first_login_modal_close")}
          aria-label="Acknowledge terms and close modal"
          disabled={isSubmitting}
          className="inline-flex size-8 items-center justify-center rounded-md text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-dark focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50">
          <XMarkIcon className="size-5" aria-hidden="true" />
        </button>
      </DialogTitle>
      <DialogBody className="space-y-4">
        <p className="text-sm text-gray-700">
          We've updated our Terms & Conditions. By continuing to use Centi, you
          acknowledge and accept the latest version
          {activeTermsVersion ? ` (${activeTermsVersion})` : ""}.
        </p>
        <div className="rounded-md border border-gray-300 bg-white p-3 text-sm">
          <label className="flex items-start gap-2">
            <input type="checkbox" checked readOnly className="mt-1 h-4 w-4" />
            <span className="text-gray-700">
              I acknowledge the Terms & Conditions and agree to continue using
              Centi.
              <Link
                href={
                  activeTermsUrl || "https://centihq.com/terms-and-conditions"
                }
                target="_blank"
                rel="noopener noreferrer"
                className="ml-1 font-medium underline text-dark">
                View Terms
              </Link>
            </span>
          </label>
        </div>
        <Button
          full
          className="!h-11"
          onClick={() => handleAcknowledge("first_login_modal_ok")}
          loading={isSubmitting}
          disabled={isSubmitting}>
          OK, Continue
        </Button>
      </DialogBody>
    </Dialog>
  );
}
